import React from 'react';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useChat,
  useConnectionState,
  useLocalParticipant,
  useRoomContext,
  useTranscriptions,
  useVoiceAssistant,
} from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';
import { MdClose, MdMic, MdMicOff } from 'react-icons/md';
import '../../assets/styles/LiveKitAssistant.css';

function resolveBackendBaseUrl() {
  const configured = process.env.REACT_APP_BACKEND_BASE_URI?.trim();

  if (configured) {
    return configured.replace(/\/+$/, '');
  }

  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5000';
  }

  return 'https://startup-website-x1s7.onrender.com';
}

function SessionConsole({ onDisconnect, onError }) {
  const room = useRoomContext();
  const connectionState = useConnectionState(room);
  const { localParticipant, isMicrophoneEnabled } = useLocalParticipant();
  const { chatMessages, send, isSending } = useChat();
  const { state: assistantState } = useVoiceAssistant();
  const transcriptions = useTranscriptions();
  const [draft, setDraft] = React.useState('');
  const [actionError, setActionError] = React.useState('');
  const transcriptEndRef = React.useRef(null);

  // Merge typed chat messages and live voice transcriptions into one sorted list
  const allItems = React.useMemo(() => {
    const chatItems = chatMessages.map((m) => ({
      key: `chat-${m.timestamp}-${m.from?.identity ?? 'unknown'}`,
      text: m.message,
      isLocal: m.from?.identity === localParticipant.identity,
      timestamp: m.timestamp,
      isVoice: false,
    }));

    const voiceItems = transcriptions
      .filter((t) => t.text.trim())
      .map((t) => ({
        key: `transcript-${t.streamInfo.id}`,
        text: t.text,
        isLocal: t.participantInfo.identity === localParticipant.identity,
        timestamp: t.streamInfo.timestamp,
        isVoice: true,
      }));

    return [...chatItems, ...voiceItems].sort((a, b) => a.timestamp - b.timestamp);
  }, [chatMessages, transcriptions, localParticipant.identity]);

  React.useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [allItems, assistantState]);

  const isConnected = connectionState === ConnectionState.Connected;

  // Auto-enable microphone when connected
  React.useEffect(() => {
    if (isConnected && !isMicrophoneEnabled) {
      localParticipant.setMicrophoneEnabled(true).catch((error) => {
        console.error('Failed to enable microphone:', error);
      });
    }
  }, [isConnected, isMicrophoneEnabled, localParticipant]);

  const toggleMicrophone = async () => {
    setActionError('');

    try {
      await localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled);
    } catch (error) {
      setActionError(error.message || 'Microphone access was blocked.');
      onError(error.message || 'Microphone access was blocked.');
    }
  };

  const handleSend = async (event) => {
    event.preventDefault();

    const message = draft.trim();
    if (!message) {
      return;
    }

    setActionError('');

    try {
      await send(message);
      setDraft('');
    } catch (error) {
      setActionError(error.message || 'Unable to send the message.');
      onError(error.message || 'Unable to send the message.');
    }
  };

  return (
    <div className="lk-agent-session">
      <div className="lk-agent-controls">
        <button
          type="button"
          className={`lk-agent-mic-btn ${isMicrophoneEnabled ? 'is-active' : ''}`}
          onClick={toggleMicrophone}
          title={isMicrophoneEnabled ? 'Mute microphone' : 'Enable microphone'}
        >
          {isMicrophoneEnabled ? <MdMic size={20} /> : <MdMicOff size={20} />}
        </button>
        <button type="button" className="lk-agent-disconnect-btn" onClick={() => {onDisconnect(); }} title="End session">
          <MdClose size={20} />
        </button>
      </div>

      <div className="lk-agent-transcript">
        {allItems.length === 0 && (
          <div className="lk-agent-empty-state">
            <p>Say something or type a message to start chatting with the AI assistant.</p>
          </div>
        )}

        {allItems.map((item) => (
          <article
            key={item.key}
            className={`lk-agent-message ${item.isLocal ? 'is-local' : 'is-remote'}${item.isVoice ? ' is-voice' : ''}`}
          >
            <p>{item.text}</p>
          </article>
        ))}

        <div ref={transcriptEndRef} />
      </div>

      {actionError && <p className="lk-agent-error">{actionError}</p>}

      <form className="lk-agent-compose" onSubmit={handleSend}>
        <input
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Type a message..."
          maxLength={400}
        />
        <button type="submit" disabled={isSending || !draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

export default function LiveKitAssistant() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [session, setSession] = React.useState(null);
  const [isStarting, setIsStarting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');

  const generateParticipantId = () => {
    const timestamp = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substr(2, 9);
    return `visitor-${timestamp}-${randomStr}`;
  };

  const startSession = React.useCallback(async () => {
    setIsStarting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${resolveBackendBaseUrl()}/api/v1/livekit/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          participantName: generateParticipantId(),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.details || payload.error || 'Unable to start the AI session.');
      }

      setSession(payload);
      setIsOpen(true);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to start the AI session.');
      setIsOpen(false);
    } finally {
      setIsStarting(false);
    }
  }, []);

  const disconnectSession = () => {
    setSession(null);
    setErrorMessage('');
  };

  return (
    <div className={`lk-agent-shell ${isOpen ? 'is-open' : ''}`}>
      <button
        type="button"
        className={`lk-agent-launcher ${session ? 'is-connected' : ''} ${isStarting ? 'is-loading' : ''}`}
        onClick={startSession}
        disabled={isStarting}
        title="Open AI Assistant"
      >
        {isStarting ? (
          <span className="lk-agent-loader" />
        ) : (
        //   <FiMessageCircle size={24} />
            <img src="/images/ai-bot-icon.png" alt="AI Assistant" className="h-8 w-8" />
        )}
      </button>

      {isOpen && (
        <section className="lk-agent-panel" aria-label="AI Assistant">
          {errorMessage && <p className="lk-agent-error">{errorMessage}</p>}

          {session && (
            <LiveKitRoom
              token={session.token}
              serverUrl={session.wsUrl}
              connect={true}
              audio={false}
              onDisconnected={() => disconnectSession()}
              onError={(error) => setErrorMessage(error.message || 'Connection failed.')}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <RoomAudioRenderer />
              <SessionConsole
                onDisconnect={() => {
                  disconnectSession();
                  setIsOpen(false);
                }}
                onError={setErrorMessage}
              />
            </LiveKitRoom>
          )}

          {isStarting && !session && (
            <div className="lk-agent-loading">
              <span className="lk-agent-loader" />
              <p>Connecting to AI Assistant...</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}